/**
 * Main chat hook that orchestrates conversation state management.
 *
 * This hook delegates responsibilities to smaller, focused hooks:
 * - useEvidenceSourceConfig: Source/domain configuration
 * - useChatBackend: Backend data fetching (single fetch owner)
 * - useChatStream: Streaming logic
 * - useChatConversation: Conversation building from local/backend timelines
 * - useChatStreamControl: Abort and retry stream operations
 * - useSessionlessChatMigration: Sessionless → chat transition
 * - useChatSendMessage: Message sending logic
 */

import { chatStateAtom, evidenceProTrialBannerQueryCountAtom, newChatModeAtom, showEvidenceProTrialBannerAtom, streamingChatIdsAtom } from '@/atoms/chatAtoms';
import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import {
  pendingSourcesSpotlightAfterStreamAtom,
  sourcesSpotlightOpenAtom,
} from '@/atoms/evidenceOnboardingAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import type {
  ChipOrigin,
  ChipSurface,
} from '@/models/analytics/features/chat';
import type { ReasoningItemEntry } from '@heidi/evidence/contracts';
import type { TimelineConversationHook } from '@/components/chat/types/types';
import { ChatMode as ChatModeEnum } from '@heidi/evidence/contracts';
import {
  IS_AUTO_MODE_ONLY_ENABLED,
  IS_EVIDENCE_SBS_ENABLED,
} from '@/features/flags/legacy-flags';
import { finishTracking, TrackingKey } from '@heidi/runtime/performance';
import { useMarkImproveNotesTaskComplete } from '@heidi/growth';
import {
  evidenceSBSChatIdAtom,
  evidenceSBSMessagesAtom,
  evidenceSBSSessionIdAtom,
  evidenceSBSSetupAction,
  evidenceSBSUserQueryAtom,
  resetEvidenceSBSAction,
  shouldDoEvidenceSBSAtom,
} from '@/features/sideBySideEvidence/atoms';
import { getCountryRaw } from '@heidi/utils/get-country';
import { logger } from '@/utils/logger/logger';
import type { EvidenceStreamErrorCode } from '@/utils/timelineEventSource';
import { getRegion } from '@/helpers/getRegion';
import { useMutateCreateChat } from '@/hooks/mutation/useMutateCreateChat';
import { invalidateGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { buildMessageHistory } from '@heidi/evidence/data';
import { generateId } from '@heidi/utils';
import { useChatBackend } from './useChatBackend';
import { useChatCompletionNotification } from './useChatCompletionNotification';
import { useChatConversation } from './useChatConversation';
import { useChatSendMessage } from './useChatSendMessage';
import { useChatStream } from './useChatStream';
import { useChatStreamControl } from './useChatStreamControl';
import { useEvidenceSourceConfig } from './useEvidenceSourceConfig';
import { applyPageLoadQueryFailedFallback } from './pageLoadRecovery';
import { useSessionlessChatMigration } from './useSessionlessChatMigration';
import { useStreamRecovery } from './useStreamRecovery';

const SESSIONLESS_KEY = 'sessionless';

export const useChat = (
  sessionId: string | null | undefined,
  content?: string,
  chatId?: string | null,
  activeTabName?: string,
  activeDocumentId?: string,
  /**
   * Remote kill switch for the linked-context provenance gate, resolved by
   * the caller (Chat.tsx holds the quarantine pair to `@/features/flags`;
   * this hook does not) and threaded into the send boundary. Optional so
   * read-only consumers (`useChatResponseActions`) are unaffected — they
   * never send, so the gate state is moot on that path.
   */
  isProvenanceGateKilled?: boolean,
  /**
   * Explicit mode for a host that is not a scribe session. Sessionless
   * chats otherwise always send EVIDENCE, and a stale LEGACY atom is
   * upgraded to AUTO. An explicit mode is sent as given.
   */
  modeOverride?: ChatModeEnum,
  /**
   * When set, a new sessionless chat stays on this page. The callback
   * receives the client chat id after create so the host can stream here
   * instead of following the redirect to `/scribe/chat`.
   */
  onHostedChatCreated?: (chatId: string) => void
): TimelineConversationHook => {
  const [chatState, setChatState] = useAtom(chatStateAtom);
  const [error, setError] = useState<string | null>(null);

  const [newMode] = useAtom(newChatModeAtom);
  const setStreamingChatIds = useSetAtom(streamingChatIdsAtom);
  const setShowEvidenceProTrialBanner = useSetAtom(showEvidenceProTrialBannerAtom);
  const setTrialBannerQueryCount = useSetAtom(evidenceProTrialBannerQueryCountAtom);
  const sourceControlToggle = useAtomValue(sourceControlToggleAtom);
  const { isInFreeTrial } = useEvidenceBillingInfo();
  const { mutateAsync: createChat, isPending: isCreatingChat } = useMutateCreateChat();

  const isSessionless = !sessionId;
  // EVI-731: Never send LEGACY mode to the backend from the frontend.
  // Users may have stale 'legacy' persisted in localStorage from when the mode toggle existed.
  // - IS_AUTO_MODE_ONLY_ENABLED: mode toggle hidden → always AUTO (backend does routing)
  // - Else: newChatModeAtom, upgrading stale LEGACY → AUTO
  const effectiveMode =
    modeOverride ??
    (isSessionless
      ? ChatModeEnum.EVIDENCE
      : IS_AUTO_MODE_ONLY_ENABLED()
        ? ChatModeEnum.AUTO
        : newMode === ChatModeEnum.LEGACY
          ? ChatModeEnum.AUTO
          : newMode);

  const evidenceVersion = 3;

  // Get all source configuration from extracted hook
  const {
    effectiveDomains,
    includeUserDocs,
    includeWebSearch,
    sourceListIds,
    userDocCollectionIds,
    collectionIds,
    integrationPartners,
    evidenceSourceFilters,
    isWorkspaceCollectionScopePending,
  } = useEvidenceSourceConfig();

  // State key must include chatId when available to separate chat states
  const stateKey = chatId
    ? `chat-${chatId}`
    : sessionId
      ? `session-${sessionId}-new`
      : SESSIONLESS_KEY;
  const analyticsSessionId = sessionId ?? SESSIONLESS_KEY;

  // Get clientChatId from state as fallback for follow-up messages
  const clientChatIdFromState = chatState[stateKey]?.metadata?.clientChatId;

  // Centralized backend data fetching
  const {
    chatData,
    backendTimeline,
    chatMeta,
    isFetching: isFetchingChat,
    error: chatError,
  } = useChatBackend(chatId);

  const handleStreamCompleteFromNotification = useChatCompletionNotification(
    chatId ?? null,
    {
      chatTitle: chatMeta.title,
      isSessionlessChat: isSessionless, // Use local context, not backend (which may not be loaded yet)
    }
  );

  const setSourcesSpotlightOpen = useSetAtom(sourcesSpotlightOpenAtom);

  const handleStreamComplete = useCallback(() => {
    handleStreamCompleteFromNotification();
    // Open Sources spotlight (step 3/3) after stream when user came from Session Chat Sidebar spotlight Next.
    if (sessionId) {
      const store = getDefaultStore();
      const pending = store.get(pendingSourcesSpotlightAfterStreamAtom);
      if (pending) {
        store.set(pendingSourcesSpotlightAfterStreamAtom, false);
        setSourcesSpotlightOpen(true);
      }
    }
  }, [
    handleStreamCompleteFromNotification,
    sessionId,
    setSourcesSpotlightOpen,
  ]);

  // Evidence analytics tracking
  const { trackChatCompleted, trackChatError, trackChatViewed, trackChatCreated } = useEvidenceAnalytics();

  const markImproveNotesTaskComplete = useMarkImproveNotesTaskComplete();

  const lastQueryLengthRef = useRef<number>(0);
  const lastChipMetaRef = useRef<{
    entryMethod?: string;
    chipLabel?: string;
    chipCategory?: string;
    chipOrigin?: ChipOrigin;
    chipSurface?: ChipSurface;
    queued?: boolean;
    queueDurationMs?: number;
  }>({});

  const handleEvidenceStreamComplete = useCallback(
    (info: {
      durationMs: number;
      timeToFirstTokenMs?: number;
      referencesCount: number;
      responseLength: number;
      tavilySearchCount?: number;
      routedMode?: string;
      toolsUsed?: string[];
      responseWordCount?: number;
      messageId?: string;
      chatId?: string;
      quotaExhaustedMessageShown?: boolean;
    }) => {
      const chipMeta = lastChipMetaRef.current;
      trackChatCompleted({
        durationMs: info.durationMs,
        timeToFirstTokenMs: info.timeToFirstTokenMs,
        referencesCount: info.referencesCount,
        queryLength: lastQueryLengthRef.current,
        responseLength: info.responseLength,
        responseWordCount: info.responseWordCount,
        chatId: info.chatId ?? chatId ?? undefined,
        messageId: info.messageId,
        sessionId: sessionId ?? undefined,
        tavilySearchCount: info.tavilySearchCount,
        routedMode: info.routedMode,
        toolsUsed: info.toolsUsed,
        entryMethod: chipMeta.entryMethod,
        chipLabel: chipMeta.chipLabel,
        chipCategory: chipMeta.chipCategory,
        chipOrigin: chipMeta.chipOrigin,
        chipSurface: chipMeta.chipSurface,
        queued: chipMeta.queued,
        queueDurationMs: chipMeta.queueDurationMs,
        quotaExhaustedMessageShown: info.quotaExhaustedMessageShown,
      });
      void invalidateGamifyTrialExtension();
      // GRO-3270: a completed in-session Ask Heidi chat response is evidence
      // of use. Sessionless Evidence chats (no sessionId) don't count.
      if (sessionId) {
        markImproveNotesTaskComplete('ask-heidi');
      }
    },
    [trackChatCompleted, chatId, sessionId, markImproveNotesTaskComplete]
  );

  const handleEvidenceStreamError = useCallback(
    (info: {
      errorMessage: string;
      durationMs?: number;
      routedMode?: string;
      errorType?: 'stream_mid' | 'stream_start';
      httpStatus?: number;
      errorCode?: EvidenceStreamErrorCode;
    }) => {
      trackChatError({
        errorMessage: info.errorMessage,
        durationMs: info.durationMs,
        queryLength: lastQueryLengthRef.current,
        chatId: chatId ?? undefined,
        sessionId: sessionId ?? undefined,
        routedMode: info.routedMode,
        errorType: info.errorType,
        httpStatus: info.httpStatus,
        errorCode: info.errorCode,
      });
    },
    [trackChatError, chatId, sessionId]
  );

  const TRIAL_BANNER_QUERY_INTERVAL = 5;
  const handleStreamStart = useCallback(() => {
    const hasClinicalSources =
      sourceControlToggle.localGuidelines || sourceControlToggle.primaryEvidence;
    const hasV2ProSources =
      sourceControlToggle.webSearch ||
      sourceControlToggle.myLibrary ||
      sourceControlToggle.webCollections ||
      Object.values(sourceControlToggle.integrationPartners).some(Boolean);
    const isUsingProFeatures = hasClinicalSources || hasV2ProSources;
    if (isInFreeTrial && isUsingProFeatures) {
      setTrialBannerQueryCount((prev) => {
        const next = prev + 1;
        if (next % TRIAL_BANNER_QUERY_INTERVAL === 0) {
          setShowEvidenceProTrialBanner(true);
        }
        return next;
      });
    }
  }, [isInFreeTrial, sourceControlToggle, setShowEvidenceProTrialBanner, setTrialBannerQueryCount]);

  // Track chat_created when the backend creates a new chat during streaming
  // (for in-session chats where there's no explicit createChat call)
  const handleChatCreated = useCallback(
    (newChatId: string) => {
      trackChatCreated(newChatId, sessionId ?? undefined);
    },
    [trackChatCreated, sessionId]
  );


  const { isRecovering, startRecovery } = useStreamRecovery({ stateKey });

  // On page load: if the backend reports an in-progress completion, start recovery
  // polling so the user sees the result without a manual reload.
  // Guard with streamingState check so this doesn't trigger during normal streaming
  // (where completion_in_progress is also true on the backend).
  const pageLoadRecoveryTriggeredRef = useRef(false);
  useEffect(() => {
    pageLoadRecoveryTriggeredRef.current = false;
  }, [chatId]);
  useEffect(() => {
    const localState = getDefaultStore().get(chatStateAtom)?.[stateKey];
    const currentlyStreaming = !!localState?.streamingState;
    const localTimeline = localState?.timeline ?? [];
    const localHasAssistantContent = localTimeline.some(
      (entry) => entry.type !== 'user_message'
    );
    // EVI-1403: skip recovery for a short window after EvidenceSBSOverlay
    // dismisses (any path: confirm, skip, error, or hung-stream backstop).
    // The backend takes ~1-3s to finalise the SBS confirm before the chat
    // refetch returns the assistant response — without this guard the user
    // sees "Reconnecting…" flicker over a chat that's about to populate
    // itself naturally. See ConversationMetadata.recentlySBSConfirmedAt.
    const SBS_CONFIRM_RECOVERY_SUPPRESSION_MS = 10_000;
    const recentlySBSConfirmedAt = localState?.metadata?.recentlySBSConfirmedAt;
    const recentlySBSConfirmed =
      recentlySBSConfirmedAt !== undefined &&
      Date.now() - recentlySBSConfirmedAt < SBS_CONFIRM_RECOVERY_SUPPRESSION_MS;
    if (
      chatId &&
      chatData?.completion_in_progress &&
      !isFetchingChat &&
      !currentlyStreaming &&
      !localHasAssistantContent &&
      !recentlySBSConfirmed &&
      !pageLoadRecoveryTriggeredRef.current
    ) {
      pageLoadRecoveryTriggeredRef.current = true;
      startRecovery(chatId, {
        onFallback: () => {
          // REG-51 / SWR-35: page-load recovery must surface an error + recovery path
          // instead of silently leaving the user with no message.
          setChatState((prev) => {
            const session = prev[stateKey];
            if (!session) return prev;

            return {
              ...prev,
              [stateKey]: applyPageLoadQueryFailedFallback(session),
            };
          });
        },
      });
    }
  }, [chatId, chatData?.completion_in_progress, isFetchingChat, startRecovery, stateKey, setChatState]);

  const { startStreaming: rawStartStreaming } = useChatStream({
    stateKey,
    analyticsSessionId,
    sessionId,
    chatId: chatId ?? clientChatIdFromState ?? null,
    mode: effectiveMode,
    version: evidenceVersion,
    content,
    domains: effectiveDomains,
    includeUserDocs,
    includeWebSearch,
    evidenceSourceFilters,
    sourceListIds,
    userDocCollectionIds,
    collectionIds,
    integrationPartners,
    isInSession: !isSessionless,
    activeTab: activeTabName,
    activeDocumentId,
    onStreamStart: handleStreamStart,
    onStreamComplete: handleStreamComplete,
    onEvidenceStreamComplete: handleEvidenceStreamComplete,
    onEvidenceStreamError: handleEvidenceStreamError,
    onChatCreated: handleChatCreated,
    onStartRecovery: startRecovery,
  });

  const startStreaming: typeof rawStartStreaming = useCallback(
    async (timeline, overrideMode, onError, activeChatId, files, attachmentIds, source) => {
      const sbsChatId = activeChatId ?? chatId ?? clientChatIdFromState ?? null;
      const hasAttachments =
        (attachmentIds?.length ?? 0) > 0 || (files?.length ?? 0) > 0;

      if (IS_EVIDENCE_SBS_ENABLED() && sbsChatId && !hasAttachments) {
        const jotai = getDefaultStore();
        try {
          jotai.set(resetEvidenceSBSAction);
          // EVI-1763: do NOT flip the SBS setup-loading skeleton on
          // here. /chat/sbs/setup runs on EVERY send to decide eligibility,
          // and most sends are not in an experiment — flipping the skeleton on
          // synchronously made the comparison overlay flash on screen and then
          // vanish the instant setup returned <2 candidates (Mike's "flashes
          // SBS content even if it isn't triggered"). Instead we keep the
          // chat's normal "thinking" loader (the optimistic streaming state
          // from useChatSendMessage) up until setup resolves, and only mount
          // the overlay once SBS actually triggered (candidates returned →
          // shouldDoEvidenceSBSAtom). On a real SBS round the overlay renders
          // directly from the comparison's own per-card streaming state.
          //
          // EVI-1680 (in-session first message): the eager-created chat
          // (useChatSendMessage `isNewInSessionSbsChat`) intentionally leaves
          // `streamingState: null` so useSessionlessChatMigration's resume isn't
          // skipped, and it previously leaned on the SBS setup skeleton to cover
          // the window while setup runs. With that skeleton gone, the first
          // in-session message had a brief loader-less flash before the stream
          // started. Surface the same optimistic "thinking" loader every other
          // send already shows so setup runs behind a normal loader. No-op on
          // every non-eager path (the loader is set upstream there) and never
          // for the sessionless standalone chat.
          if (!isSessionless) {
            setChatState((prev) => {
              const existingState = prev[stateKey];
              if (!existingState || existingState.streamingState) return prev;
              const hasPlaceholder = existingState.timeline.some(
                (entry) => entry.type === 'reasoning_item' && entry.backendIndex === -1
              );
              if (hasPlaceholder) return prev;
              const lastUserIndex = existingState.timeline.reduce(
                (acc, entry, idx) => (entry.type === 'user_message' ? idx : acc),
                -1
              );
              const placeholderPosition = lastUserIndex + 1;
              const optimisticNow = Date.now();
              const placeholder: ReasoningItemEntry = {
                type: 'reasoning_item',
                id: generateId(),
                content: [],
                position: placeholderPosition,
                backendIndex: -1,
              };
              return {
                ...prev,
                [stateKey]: {
                  ...existingState,
                  timeline: [...existingState.timeline, placeholder],
                  streamingState: {
                    basePosition: placeholderPosition,
                    activeIndices: new Map(),
                    currentActiveIndex: null,
                    activityGroupOpen: true,
                    streamStartedAt: optimisticNow,
                    lastChunkAt: optimisticNow,
                    lastVisibleContentAt: optimisticNow,
                  },
                  metadata: {
                    ...existingState.metadata,
                    lastActivity: optimisticNow,
                  },
                },
              };
            });
          }

          const triggerLocation = isSessionless ? 'out_of_session' : 'in_session';

          await jotai.set(evidenceSBSSetupAction, {
            chat_id: sbsChatId,
            message_index: timeline.length,
            session_id: sessionId ?? null,
            evidence_trigger_location: triggerLocation as 'in_session' | 'out_of_session',
            country: getCountryRaw(),
            region: getRegion(),
          });

          if (jotai.get(shouldDoEvidenceSBSAtom)) {
            const lastUserMsg = [...timeline].reverse().find((e) => e.type === 'user_message');
            if (lastUserMsg && 'content' in lastUserMsg) {
              jotai.set(evidenceSBSUserQueryAtom, lastUserMsg.content as string);
            }
            jotai.set(evidenceSBSMessagesAtom, buildMessageHistory(timeline));
            jotai.set(evidenceSBSChatIdAtom, sbsChatId);
            jotai.set(evidenceSBSSessionIdAtom, sessionId ?? null);
            // EVI-1507: Clear the optimistic streaming state set by useChatSendMessage
            // so the background chat doesn't show a stale loader while the SBS overlay
            // is active. The SBS overlay manages its own stream display independently.
            setChatState((prev) => {
              const existingState = prev[stateKey];
              if (!existingState?.streamingState) return prev;
              return {
                ...prev,
                [stateKey]: {
                  ...existingState,
                  timeline: existingState.timeline.filter(
                    (entry) => !(entry.type === 'reasoning_item' && entry.backendIndex === -1)
                  ),
                  streamingState: null,
                },
              };
            });
            return;
          }
        } catch (error) {
          logger.warn('[EVIDENCE_SBS] setup failed, falling back to normal streaming', { error });
        }
      }

      await rawStartStreaming(timeline, overrideMode, onError, activeChatId, files, attachmentIds, source);
    },
    [rawStartStreaming, chatId, clientChatIdFromState, isSessionless, sessionId, stateKey, setChatState]
  );

  // Extract state slices
  const currentChatState = chatState[stateKey];
  const sessionlessState = chatState[SESSIONLESS_KEY];
  // Fallback state for session-based chat key transitions.
  // When a chat is auto-created during protocol execution, the chatId prop
  // eventually changes (via useAutoSelectRecentSessionChat), shifting the
  // stateKey from `session-{sessionId}-new` to `chat-{chatId}`. Until the
  // migration effect below runs, the new key has no data. We read the old key
  // here so the conversation memo can use it as a fallback to avoid flashing
  // the EvidenceChatSkeleton.
  const sessionNewKey = sessionId ? `session-${sessionId}-new` : null;
  const sessionNewState = sessionNewKey ? chatState[sessionNewKey] : undefined;

  // Track previous stateKey for state migration on key transitions
  const prevStateKeyRef = useRef(stateKey);

  // Migrate chat state when stateKey changes due to an auto-created chatId
  // during protocol execution. This shifts the key from `session-{id}-new`
  // to `chat-{chatId}` within the SAME session.
  // IMPORTANT: Only migrate for this specific transition. Do NOT migrate when
  // the user navigates to a new session or clicks "New chat" — those should
  // start with a clean state.
  useLayoutEffect(() => {
    const prevKey = prevStateKeyRef.current;
    prevStateKeyRef.current = stateKey;

    if (prevKey === stateKey) return;

    // Only migrate when a chatId appeared in the current session:
    // old key = `session-{sessionId}-new`, new key = `chat-{chatId}`
    const isSessionToChatTransition =
      sessionId &&
      prevKey === `session-${sessionId}-new` &&
      stateKey.startsWith('chat-');

    if (!isSessionToChatTransition) return;

    setChatState((prev) => {
      const oldState = prev[prevKey];
      const newState = prev[stateKey];

      // Only migrate if old key has meaningful data and new key is empty
      const oldHasData =
        oldState && (oldState.timeline.length > 0 || oldState.streamingState);
      const newIsEmpty =
        !newState || (newState.timeline.length === 0 && !newState.streamingState);

      if (oldHasData && newIsEmpty) {
        const result = { ...prev, [stateKey]: oldState };
        delete result[prevKey];
        return result;
      }
      return prev;
    });
  }, [stateKey, sessionId, setChatState]);

  // Build conversation from local/backend timelines
  const { conversation } = useChatConversation({
    chatId,
    sessionId,
    stateKey,
    currentChatState,
    sessionNewState,
    sessionlessState,
    backendTimeline,
    chatMeta,
    setChatState,
  });

  // Handle sessionless → chat migration (and EVI-1680 in-session SBS resume)
  useSessionlessChatMigration({
    chatId,
    sessionId,
    stateKey,
    isSessionless,
    effectiveMode,
    effectiveDomains,
    sessionlessState,
    sessionNewState,
    sessionNewKey,
    currentChatStreamingState: currentChatState?.streamingState,
    setChatState,
    startStreaming,
    setError,
    lastQueryLengthRef,
  });

  // Message sending
  const { sendMessage } = useChatSendMessage({
    chatId,
    sessionId,
    isSessionless,
    stateKey,
    analyticsSessionId,
    effectiveMode,
    effectiveDomains,
    conversationTimeline: conversation.timeline,
    setChatState,
    startStreaming,
    createChat,
    setError,
    lastQueryLengthRef,
    lastChipMetaRef,
    isProvenanceGateKilled,
    stayOnPage: Boolean(onHostedChatCreated),
    onHostedChatCreated,
  });

  const clearConversation = useCallback(() => {
    setChatState((prev) => ({
      ...prev,
      [stateKey]: {
        timeline: [],
        streamingState: null,
        metadata: { lastActivity: Date.now() },
      },
    }));
    setError(null);
  }, [stateKey, setChatState]);

  // Reset state for starting a new session chat.
  // Unlike clearConversation (which clears the CURRENT stateKey — i.e. the
  // old chat the user is viewing), this clears only the session-new key to
  // ensure a clean slate for the upcoming "new chat". The old chat's
  // in-memory state is preserved so the user can navigate back to it
  // without hitting a skeleton/loading state.
  const resetForNewChat = useCallback(() => {
    if (sessionId) {
      const newChatKey = `session-${sessionId}-new`;
      setChatState((prev) => {
        const existing = prev[newChatKey];
        if (existing && (existing.timeline.length > 0 || existing.streamingState)) {
          return {
            ...prev,
            [newChatKey]: {
              timeline: [],
              streamingState: null,
              metadata: { lastActivity: Date.now() },
            },
          };
        }
        return prev;
      });
    }
    setError(null);
  }, [sessionId, setChatState]);

  // Stream control (abort, retry)
  const { abortStream, retryLastMessage } = useChatStreamControl({
    stateKey,
    analyticsSessionId,
    chatId,
    sessionId,
    conversation,
    setChatState,
    startStreaming,
  });

  const messageHistory = useMemo(
    () => buildMessageHistory(conversation.timeline),
    [conversation.timeline]
  );
  const isStreaming = !!conversation.streamingState;

  // Update streaming chat IDs atom
  useEffect(() => {
    if (isStreaming && chatId) {
      setStreamingChatIds((prev) => {
        const next = new Set(prev);
        next.add(chatId);
        return next;
      });
    } else if (!isStreaming && chatId) {
      setStreamingChatIds((prev) => {
        const next = new Set(prev);
        next.delete(chatId);
        return next;
      });
    }
  }, [isStreaming, chatId, setStreamingChatIds]);

  // Track chat viewed event when an existing chat is loaded
  // When in a session context, only fire if the Ask Heidi sidebar is actually open.
  // This prevents chat_viewed from firing when ChatRenderer stays mounted but hidden
  // (e.g., mobile drawer with destroyOnClose=false).
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isAskHeidiSidebarOpen = sidebarOpenTab === SidebarTab.ASK_HEIDI_V2;
  const isChatVisibleInSession = !sessionId || isAskHeidiSidebarOpen;

  const trackedChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Only track if:
    // 1. We have a chatId (existing chat, not new)
    // 2. We've finished loading (not fetching)
    // 3. We haven't already tracked this chatId
    // 4. The chat is actually visible (not hidden behind a closed sidebar/drawer)
    if (chatId && !isFetchingChat && isChatVisibleInSession && trackedChatIdRef.current !== chatId) {
      trackedChatIdRef.current = chatId;
      trackChatViewed(chatId, sessionId ?? undefined);
      finishTracking({
        key: TrackingKey.EVIDENCE_CHAT_OPEN,
        metadata: {
          chat_id: chatId,
          session_id: sessionId ?? null,
          result: chatError ? 'error' : 'success',
        },
      });
    }
  }, [chatId, isFetchingChat, isChatVisibleInSession, sessionId, trackChatViewed, chatError]);

  return {
    conversation,
    messageHistory,
    sendMessage,
    isStreaming,
    isRecovering,
    clearConversation,
    resetForNewChat,
    error: error || (chatError ? 'Failed to load chat' : null),
    abortStream,
    retryLastMessage,
    isFetchingChat,
    chatError,
    isCreatingChat,
    chatMeta,
    isWorkspaceCollectionScopePending,
  };
};
